import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { KeyRound, LogOut, Check, X } from "lucide-react";

type Rule = { label: string; ok: boolean };

const buildRules = (pwd: string): Rule[] => [
  { label: "Al menos 12 caracteres", ok: pwd.length >= 12 },
  { label: "Una letra mayúscula (A-Z)", ok: /[A-Z]/.test(pwd) },
  { label: "Una letra minúscula (a-z)", ok: /[a-z]/.test(pwd) },
  { label: "Un número (0-9)", ok: /\d/.test(pwd) },
  { label: "Un símbolo (!@#$%&*?_-)", ok: /[^A-Za-z0-9]/.test(pwd) },
  {
    label: "No usar palabras comunes (password, 123456, qwerty, admin, contraseña…)",
    ok: pwd.length > 0 && !/(password|contrase|123456|qwerty|admin|letmein|welcome|abc123|iloveyou)/i.test(pwd),
  },
];

const translateAuthError = (msg: string): { title: string; description: string } => {
  const m = msg.toLowerCase();
  if (m.includes("pwned") || m.includes("compromised") || m.includes("breach") || m.includes("data breach")) {
    return {
      title: "Esta contraseña aparece en filtraciones públicas",
      description:
        "El sistema bloquea contraseñas conocidas (Have I Been Pwned). Crea una única que NO hayas usado en otros sitios: 12+ caracteres, mayúsculas, minúsculas, números y un símbolo. Evita palabras comunes y patrones obvios.",
    };
  }
  if (m.includes("weak") || m.includes("strength") || m.includes("requirements")) {
    return {
      title: "Contraseña no suficientemente segura",
      description:
        "Usa al menos 12 caracteres con mayúsculas, minúsculas, números y símbolos. Evita palabras de diccionario, fechas y secuencias (123, abc).",
    };
  }
  if (m.includes("short") || m.includes("length") || m.includes("at least")) {
    return { title: "Contraseña demasiado corta", description: "Mínimo 12 caracteres recomendado." };
  }
  if (m.includes("same") || m.includes("different from")) {
    return { title: "Reutilizas la contraseña", description: "La nueva contraseña debe ser distinta a la anterior." };
  }
  return { title: "No se pudo actualizar la contraseña", description: msg };
};

const ChangePassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, clearMustChangePassword, signOut } = useAuth();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const rules = useMemo(() => buildRules(password), [password]);
  const allRulesOk = rules.every((r) => r.ok);
  const matches = password.length > 0 && password === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!allRulesOk) {
      toast({
        title: "La contraseña no cumple las directrices",
        description: "Revisa los requisitos marcados en rojo y vuelve a intentarlo.",
        variant: "destructive",
      });
      return;
    }
    if (password !== confirmPassword) {
      toast({
        title: "Las contraseñas no coinciden",
        description: "Revisa ambos campos antes de continuar.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setLoading(false);
      const t = translateAuthError(error.message ?? "");
      toast({ title: t.title, description: t.description, variant: "destructive" });
      return;
    }

    // RLS prevents the user from updating must_change_password directly,
    // so we call a SECURITY DEFINER function that clears only their own flag.
    const { error: clearErr } = await supabase.rpc("clear_my_must_change_password" as any);
    if (clearErr) {
      setLoading(false);
      toast({
        title: "Contraseña cambiada pero no se pudo cerrar el bucle",
        description: clearErr.message,
        variant: "destructive",
      });
      return;
    }
    clearMustChangePassword();
    setLoading(false);

    toast({ title: "Contraseña actualizada", description: "Ya puedes usar la aplicación con tu nueva contraseña." });
    navigate("/app", { replace: true });
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6 py-10">
      <div className="w-full max-w-md space-y-6">
        <div className="space-y-2 text-center">
          <div className="flex justify-center">
            <KeyRound className="h-8 w-8 text-[hsl(var(--brand-green))]" />
          </div>
          <h1 className="text-2xl font-semibold text-foreground">Cambia tu contraseña</h1>
          <p className="text-sm text-muted-foreground">
            Por seguridad, sustituye la contraseña provisional por una nueva que cumpla todas las directrices.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
          <div className="space-y-1.5">
            <Label htmlFor="newPassword">Nueva contraseña</Label>
            <Input
              id="newPassword"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              minLength={12}
              autoComplete="new-password"
              data-no-uppercase
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirmPassword">Repetir contraseña</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              required
              minLength={12}
              autoComplete="new-password"
              data-no-uppercase
            />
            {confirmPassword.length > 0 && !matches && (
              <p className="text-xs text-destructive">Las contraseñas no coinciden.</p>
            )}
          </div>

          <div className="rounded-md border border-border bg-muted/30 p-3 space-y-1.5">
            <p className="text-xs font-semibold text-foreground">Directrices de seguridad</p>
            <ul className="space-y-1">
              {rules.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-xs">
                  {r.ok ? (
                    <Check className="h-3.5 w-3.5 mt-0.5 text-[hsl(var(--brand-green))] shrink-0" />
                  ) : (
                    <X className="h-3.5 w-3.5 mt-0.5 text-destructive shrink-0" />
                  )}
                  <span className={r.ok ? "text-foreground" : "text-muted-foreground"}>{r.label}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-muted-foreground pt-1 border-t border-border mt-2">
              Además, el sistema rechaza contraseñas que aparezcan en filtraciones públicas conocidas. Usa una única, que no hayas reutilizado en otros sitios.
            </p>
          </div>

          <Button
            type="submit"
            disabled={loading || !allRulesOk || !matches}
            className="w-full bg-[hsl(var(--brand-green))] hover:bg-[hsl(var(--brand-green))]/90 text-white font-semibold"
          >
            {loading ? "Guardando…" : "Guardar y continuar"}
          </Button>
        </form>

        <button
          type="button"
          onClick={handleSignOut}
          className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors inline-flex items-center justify-center gap-1.5"
        >
          <LogOut className="h-3 w-3" /> Cerrar sesión
        </button>
      </div>
    </div>
  );
};

export default ChangePassword;
